import { CommonDialogs } from "@mcleod/common";
import { DataSource, DropdownItem, Layout, Snackbar } from "@mcleod/components";
import { Api, DateUtil, FileUtil, ModelRow, NumberUtil } from "@mcleod/core";
import { LMECompanySettings, LMEGeneralSettings } from "@mcleod/general";
import { RowBrltlOrderFreightGroupItem } from "../models/ModelBrltlOrderFreightGroupItem";
import { RowBrltlOrderFreightGroupItemFields } from "../models/autogen/AutogenModelBrltlOrderFreightGroupItem";

export interface BRLTLOrder {
    revenue_code_id: string;
    order_type_id: string;
    order_mode?: string,
    no_freight_group_items?: boolean;
}

export class BrltlUtil {
    public static readonly freightItemTableFields = {
        hazmatFields: [
            "hazmat", "hazmat_number", "hazmat_class_code", "hazmat_emergency_number", "hazmat_reference_data",
            "hazmat_is_residue", "poison_zone", "is_poison", "hazmat_qty_code", "placard_required",
            "hazmat_ref_type_code", "hazmat_subsidiary_code", "hazmat_unna_number", "hazmat_packing_group",
            "lookup_hazmat_packing_group", "hazmat_proper_shipname", "hazmat_erg_number"
        ],
        hazmatLookupFields: ["hazmat_class_code"],
        commodityFields: [
            "nmfc_class_code", "fgi_packaging_type_code", "length", "height", "width", "description",
            "nmfc_code", "weight", "weight_uom_type_code", "req_spots", "handling_units", "product_sku", "fgi_value"
        ],
        commodityLookupFields: ["nmfc_class_code", "fgi_packaging_type_code", "nmfc_code"],
        dimensionFields: ["length", "width", "height"],
        requiredFields: ["weight", "req_spots", "nmfc_class_code"]
    };

    public static clearFieldsOnCommoditySelected(row: ModelRow) {
        BrltlUtil.freightItemTableFields.commodityLookupFields.forEach(field => row.removeLookupModelData(field));
        BrltlUtil.freightItemTableFields.commodityFields.forEach(field => row.set(field, null));
        BrltlUtil.freightItemTableFields.hazmatLookupFields.forEach(field => row.removeLookupModelData(field));
        BrltlUtil.freightItemTableFields.hazmatFields.forEach(field => row.set(field, null));
    }

    public static getTotalFormattedWeight(dataSource: DataSource<RowBrltlOrderFreightGroupItem>): string {
        return NumberUtil.formatDecimal(this.getTotalWeight(dataSource) ?? 0, "#,###.#") + " lbs"
    }

    public static getTotalWeight(dataSource: DataSource<RowBrltlOrderFreightGroupItem>): number {
        return this.getFgiTotal(dataSource, "weight");
    }

    public static formatTotalWeight(weight: number): string {
        return NumberUtil.formatDecimal(weight ?? 0, "#,###.#") + " lbs"
    }

    public static isCompanySettingsBrokerageLTL() {
        return LMECompanySettings.getSingleton().isBrokerageLtl();
    }

    public static isBRLTLOrder(data: BRLTLOrder): boolean {
        return LMECompanySettings.getSingleton().isBrokerageLtl() && this.shouldRateUsingTariff(data);
    }

    public static shouldRateUsingTariff(data: BRLTLOrder): boolean {
        const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
        let rate_codes = "";
        if (dispatchControl.rate_using_tariff_codes != undefined) {
            rate_codes = dispatchControl.rate_using_tariff_codes;
        }

        switch (dispatchControl.rate_using_tariff) {
            case "A":
                return true;
            case "L":
                return data.order_mode == "L";
            case "P":
                return data.order_mode == "P";
            case "O":
                return this.verifyTariffCodes(data.order_type_id, rate_codes);
            case "R":
                return this.verifyTariffCodes(data.revenue_code_id, rate_codes);
        }
    }

    private static verifyTariffCodes(field: string, codes: string) {
        return codes.split(",").findIndex(code => code.trim() == field) >= 0;
    }

    public static getFgiTotal(dataSource: DataSource<RowBrltlOrderFreightGroupItem>, field: keyof RowBrltlOrderFreightGroupItemFields): number {
        const data = dataSource?.data;
        if (data != null && data.length > 0) {
            return data.map(row => {
                const value = parseFloat(row.get(field, 0).toString().replace(/,/g, ''));
                if (field !== "weight")
                    return value;
                const uom = row.get("weight_uom_type_code" as keyof RowBrltlOrderFreightGroupItemFields, "LBS");
                return (uom === "KGS") ? value * 2.20462 : value;
            }).reduce((prev, next) => prev + next, 0);
        }
        return 0;
    }

    public static getPickupDateItems(date: any): DropdownItem[] {
        const stopDate = DateUtil.parseDateTime(date);
        const timeItems: DropdownItem[] = [];
        const companySettings = LMECompanySettings.get();
        let start = new Date(companySettings.earliest_pickup);
        let end = new Date(companySettings.ord_latest_pickup_time);
        if (isNaN(start.getTime())) {
            start = new Date(stopDate);
            start.setHours(8, 0, 0, 0);
        }
        else {
            const temp = start;
            start = new Date(stopDate);
            start.setHours(temp.getHours(), temp.getMinutes(), 0, 0);
        }
        if (isNaN(end.getTime())) {
            end = new Date(stopDate);
            end.setHours(17, 0, 0, 0);
        }
        else {
            const temp = end;
            end = new Date(stopDate);
            end.setHours(temp.getHours(), temp.getMinutes(), 0, 0);
        }
        for (let d = start; d <= end; d.setMinutes(d.getMinutes() + 30)) {
            const value = DateUtil.formatDateTime(d, "HH:mm");
            timeItems.push(new DropdownItem(d.toString(), value));
        }
        return timeItems;
    }

    public static downloadBOL(orderId: string) {
        if (orderId != null) {
            const todaysDate = DateUtil.formatDate(new Date(), "MM/dd/yyyy");
            const fileName = `Order ${orderId}-BOL-${todaysDate}.pdf`;
            const progressSnack: Snackbar = Snackbar.showDownloadSnackbar("Bill of Lading", "We are generating your Bill of Lading and it will download automatically.", { persist: true });
            Api.post("portal/reports/bill-of-lading", { order_id: orderId }).then(response => {
                const report = response?.data[0]?.bol_report;
                if (report != null) {
                    FileUtil.downloadBase64AsFile(report, fileName);
                    Snackbar.showSnackbar("Downloaded " + fileName);
                    progressSnack.dismiss();
                }
            });
        }
    }

    public static async getTerminalInfo(orderId: string) {
        const layout = Layout.getLayout("lme/powerbroker/TerminalInfo");
        layout.addLayoutLoadListener(async () => {
            const response = await layout.search({ order_id: orderId })
            if (response == null) return; // error was likely thrown from the server side and a dialog should have been shown automatically
            const errorReason = layout.activeRow.get("error_reason", null);
            if (errorReason != null) {
                Snackbar.showWarningSnackbar(errorReason);
            }
            else {
                CommonDialogs.showDialog(layout, { title: "Terminal Info", panelContentProps: { width: 600 } });
            }
        });
    }
}
